import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  type ReactNode,
  // useRef,
} from "react";
import {
  Box,
  Typography,
  Divider,
  Grid2,
  Chip,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
  Alert,
  Paper,
  TableContainer,
  // IconButton,
  Stack,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  alpha,
  useTheme,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import paymentFailureStyles from "./payment-failure-dialog.module.scss";
import {
  ArtButton,
  ArtDialog,
  ArtTextField,
  useApiService,
  APIService,
  // ArtTableHandle,
  useIdpService,
  ArtPhoneInput,
} from "@art/shared-ui";
import {
  CreditCard,
  ExpandMore,
  LocationOn,
  EditOutlined,
  ForumOutlined,
  SmartToyOutlined,
  StorageOutlined,
  StorefrontOutlined,
  AppsOutlined,
} from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import { ART_URL } from "../../../Constant";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArtStore, { updateLicenseState } from "../../../store";
import { useDispatch } from "react-redux";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { getCategoryIcon, formatFeatureCode } from "../../../utils/addon-utils";
import { GetCountries, GetState } from "react-country-state-city";
import { isValidPhoneNumber } from "libphonenumber-js";
import {
  isSandboxLicense,
  licenseStateFromProfile,
} from "../../../utils/license";

const createUuid = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof (crypto as any).randomUUID === "function"
  ) {
    return (crypto as any).randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const buildPaymentAttemptStorageKey = (
  type: "subscription" | "addon",
  tenantName: string,
  firstId: string,
  secondId: string,
) => `payment_attempt:${type}:${tenantName}:${firstId}:${secondId}`;

export const getPaymentAttemptKey = (storageKey: string) => {
  try {
    const existing = sessionStorage.getItem(storageKey);
    if (existing) {
      return existing;
    }
  } catch (error) {
    console.warn(
      "Unable to read payment attempt key from sessionStorage",
      error,
    );
  }

  const newKey = createUuid();
  try {
    sessionStorage.setItem(storageKey, newKey);
  } catch (error) {
    console.warn(
      "Unable to persist payment attempt key to sessionStorage",
      error,
    );
  }

  return newKey;
};

export const clearPaymentAttemptKey = (storageKey: string) => {
  try {
    sessionStorage.removeItem(storageKey);
  } catch (error) {
    console.warn(
      "Unable to clear payment attempt key from sessionStorage",
      error,
    );
  }
};

export const APP_CATEGORY_META: Record<string, { icon: ReactNode; description: string }> = {
  LIVE_CONNECT: {
    icon: <ForumOutlined sx={{ fontSize: 26 }} />,
    description: "Real-time messaging, channels & connections",
  },
  AGENT_LAB: {
    icon: <SmartToyOutlined sx={{ fontSize: 26 }} />,
    description: "Build and orchestrate AI agents",
  },
  DATALAKE: {
    icon: <StorageOutlined sx={{ fontSize: 26 }} />,
    description: "Managed data storage, transfer and API capacity",
  },
  MARKETPLACE: {
    icon: <StorefrontOutlined sx={{ fontSize: 26 }} />,
    description: "Marketplace integrations & add-ons",
  },
};
export const DEFAULT_APP_CATEGORY_META = {
  icon: <AppsOutlined sx={{ fontSize: 26 }} />,
  description: "Manage plans for this application",
};

export const BillingAddressFormDialog = ({
  onSave,
  onCancel,
  initialAddress,
}: any) => {
  const apiservice = useApiService();
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [address, setAddress] = useState({
    line1: initialAddress?.line1 || "",
    line2: initialAddress?.line2 || "",
    city: initialAddress?.city || "",
    state: initialAddress?.state || "",
    postal_code: initialAddress?.postal_code || "",
    country: initialAddress?.country || "",
    contact: initialAddress?.contact || "",
  });
  const [errors, setErrors] = useState<any>({});
  const [gstNumber, setGstNumber] = useState(initialAddress?.gstNumber || "");

  useEffect(() => {
    GetCountries().then((data) => setCountries(data));
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    if (!address.country) {
      setStates([]);
      return;
    }
    const country = countries.find((c) => c.name === address.country);
    if (country) {
      GetState(country.id).then((data) => setStates(data));
    } else {
      setStates([]);
    }
  }, [address.country, countries]);

  const handleChange = (field: string, value: string) => {
    setAddress((prev) => {
      const updated = { ...prev, [field]: value };
      // Reset state when country changes
      if (field === "country") {
        updated.state = "";
      }
      return updated;
    });
    setErrors((prev: any) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const newErrors: any = {};
    if (!address.line1) newErrors.line1 = "Address Line 1 is required";
    if (!address.city) newErrors.city = "City is required";
    if (!address.state) newErrors.state = "State is required";
    if (!address.country) newErrors.country = "Country is required";
    if (!address.postal_code) newErrors.postal_code = "Postal code is required";
    if (!address.contact) {
      newErrors.contact = "Contact number is required";
    } else if (!isValidPhoneNumber(address.contact)) {
      newErrors.contact = "Enter a valid contact number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const response = await apiservice.put(
        "/organisation/billing-address",
        address,
      );

      if (response?.status === 200 || response?.status === 201) {
        if (gstNumber.trim()) {
          try {
            await apiservice.put("/organisation/gst-number", {
              gst_number: gstNumber.trim(),
            });
          } catch (gstError) {
            console.error("Error saving GST number:", gstError);
          }
        }
        apiservice.showSnack(
          response?.data?.message || "Billing address saved",
          "success",
        );
        onSave({ ...address, gstNumber: gstNumber.trim() });
      }
    } catch (error) {
      console.error("Error saving billing address:", error);
      apiservice.showSnack("Failed to save billing address", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <LocationOn color="primary" />
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {initialAddress?.line1
              ? "Update Billing Address"
              : "Add Billing Address"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A billing address is required before you can proceed with the
            purchase.
          </Typography>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12 }}>
            <ArtTextField
              dataQa="line1"
              label="Address Line 1"
              value={address.line1}
              onChange={(e: any) => handleChange("line1", e.target.value)}
              marginBottom={0}
              error={errors.line1}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <ArtTextField
              dataQa="line2"
              label="Address Line 2"
              value={address.line2}
              onChange={(e: any) => handleChange("line2", e.target.value)}
              marginBottom={0}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <ArtTextField
              dataQa="city"
              label="City"
              value={address.city}
              onChange={(e: any) => handleChange("city", e.target.value)}
              marginBottom={0}
              error={errors.city}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Country"
              value={address.country}
              onChange={(e: any) => handleChange("country", e.target.value)}
              error={!!errors.country}
              helperText={errors.country}
            >
              {countries.map((c) => (
                <MenuItem key={c.id} value={c.name}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="State / Province"
              value={address.state}
              onChange={(e) => handleChange("state", e.target.value)}
              error={!!errors.state}
              helperText={errors.state}
              disabled={!address.country || states.length === 0}
            >
              {states.map((s) => (
                <MenuItem key={s.id} value={s.name}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <ArtTextField
              dataQa="postal_code"
              label="Postal Code"
              value={address.postal_code}
              onChange={(e: any) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                handleChange("postal_code", val);
              }}
              error={errors.postal_code}
              type="text"
              marginBottom={0}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <ArtTextField
              dataQa="gst-number"
              label="GST Number (Optional)"
              value={gstNumber}
              onChange={(e: any) => setGstNumber(e.target.value)}
              marginBottom={0}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.5,
                  color: "text.secondary",
                  fontSize: "0.75rem",
                }}
              >
                Contact Number
              </Typography>
              <ArtPhoneInput
                defaultCountry="IN"
                placeholder="Enter phone number"
                value={address.contact}
                onChange={(val) => handleChange("contact", val || "")}
                error={!!errors.contact}
              />
              {errors.contact && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 0.5, fontSize: "0.75rem" }}
                >
                  {errors.contact}
                </Typography>
              )}
            </Box>
          </Grid2>
        </Grid2>

        <Box
          display="flex"
          justifyContent={{ xs: "stretch", sm: "flex-end" }}
          flexDirection={{ xs: "column-reverse", sm: "row" }}
          gap={1.5}
          mt={3}
        >
          {onCancel && (
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              px: 4,
              width: { xs: "100%", sm: "auto" },
              whiteSpace: "nowrap",
            }}
          >
            {saving ? "Saving..." : "Save & Continue"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export const OrderSummaryDialog = ({
  selectedPlan,
  currentPlan,
  onProceedToPayment,
  onBack,
}: any) => {
  const theme = useTheme();
  const apiservice = useApiService();

  const planPrice = selectedPlan?.price || 0;
  const currency = selectedPlan?.currency || "USD";

  const [gstNumber, setGstNumber] = useState<string>("");
  const [editingGst, setEditingGst] = useState(false);
  const [gstInput, setGstInput] = useState("");
  const [savingGst, setSavingGst] = useState(false);

  const [billingAddress, setBillingAddress] = useState<any>(null);
  const [loadingAddress, setLoadingAddress] = useState(true);
  const [editingAddress, setEditingAddress] = useState(false);

  const fetchGstNumber = async () => {
    try {
      const res = await apiservice.get("/organisation/gst-number", { showErrorSnack: false });
      const gst = res?.data?.data?.gst_number || "";
      setGstNumber(gst);
      setGstInput(gst);
    } catch (error) {
      console.error("Failed to fetch GST number:", error);
    }
  };

  const handleSaveGst = async () => {
    setSavingGst(true);
    try {
      await apiservice.put("/organisation/gst-number", {
        gst_number: gstInput.trim(),
      });
      setGstNumber(gstInput.trim());
      setEditingGst(false);
      apiservice.showSnack("GST number updated successfully", "success");
    } catch (error) {
      console.error("Failed to save GST number:", error);
      apiservice.showSnack("Failed to update GST number", "error");
    } finally {
      setSavingGst(false);
    }
  };

  const handleClearGst = async () => {
    setSavingGst(true);
    try {
      await apiservice.put("/organisation/gst-number", { gst_number: "" });
      setGstNumber("");
      setGstInput("");
      setEditingGst(false);
      apiservice.showSnack("GST number cleared successfully", "success");
    } catch (error) {
      console.error("Failed to clear GST number:", error);
      apiservice.showSnack("Failed to clear GST number", "error");
    } finally {
      setSavingGst(false);
    }
  };
  // Payment preview state
  const [paymentPreview, setPaymentPreview] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);

  const fetchBillingAddress = async () => {
    setLoadingAddress(true);
    try {
      const response = await apiservice.get("/organisation/billing-address");
      if (response?.status === 200 && response.data?.data) {
        const addr = response.data.data;
        if (addr.line1) {
          setBillingAddress(addr);
          if (!addr.contact || !isValidPhoneNumber(addr.contact)) {
            setEditingAddress(true);
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch billing address:", error);
    } finally {
      setLoadingAddress(false);
    }
  };

  // Fetch payment preview (proration details)
  const fetchPaymentPreview = async () => {
    setLoadingPreview(true);
    try {
      const response = await apiservice.post(`payment/subscription/preview`, {
        license_plan_code: selectedPlan?.type,
        pricing_id: selectedPlan?.priceId,
      });
      if (response?.status === 200) {
        setPaymentPreview(response.data.data);
      }
    } catch (error: any) {
      apiservice.showSnack(
        error?.response?.data?.message || "Failed to load payment details",
        "error",
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    fetchBillingAddress();
    fetchGstNumber();
    fetchPaymentPreview();
  }, []);

  // Derive amounts from preview
  const isUpgrade = paymentPreview?.is_upgrade || false;
  const basePrice = paymentPreview?.base_price
    ? (paymentPreview.base_price / 100).toFixed(2)
    : planPrice;
  const taxPercent = paymentPreview?.tax_percent || 0;
  const taxAmount = paymentPreview?.tax_amount
    ? (paymentPreview.tax_amount / 100).toFixed(2)
    : "0.00";
  const amountToPay = paymentPreview
    ? (paymentPreview.amount_to_pay / 100).toFixed(2)
    : planPrice;
  const proratedCredit = paymentPreview?.prorated_credit
    ? (paymentPreview.prorated_credit / 100).toFixed(2)
    : null;
  const newPlanAmount = paymentPreview?.new_plan_amount
    ? (paymentPreview.new_plan_amount / 100).toFixed(2)
    : planPrice;

  // ... keep existing billingAddress form checks ...

  if (!loadingAddress && !billingAddress && !editingAddress) {
    return (
      <BillingAddressFormDialog
        onSave={(saved: any) => {
          setBillingAddress(saved);
          if (saved?.gstNumber) {
            setGstNumber(saved.gstNumber);
            setGstInput(saved.gstNumber);
          }
          fetchBillingAddress();
          fetchGstNumber();
        }}
        onCancel={onBack}
      />
    );
  }

  if (editingAddress) {
    return (
      <BillingAddressFormDialog
        initialAddress={billingAddress}
        onSave={(saved: any) => {
          setBillingAddress(saved);
          if (saved?.gstNumber) {
            setGstNumber(saved.gstNumber);
            setGstInput(saved.gstNumber);
          }
          setEditingAddress(false);
          fetchBillingAddress();
          fetchGstNumber();
        }}
        onCancel={() => setEditingAddress(false)}
      />
    );
  }

  return (
    <Box>
      {/* Back button */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          variant="text"
          size="small"
          onClick={onBack}
          sx={{ textTransform: "none", fontWeight: 500, mr: 1 }}
        >
          ← Back to Plans
        </Button>
      </Box>

      <Grid2 container spacing={3}>
        {/* Left: Plan Details + Billing Address */}
        <Grid2 size={{ xs: 12, md: 7 }}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 1, fontWeight: 600 }}
            >
              {isUpgrade ? "Upgrading To" : "Subscribing To"}
            </Typography>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
              mb={2}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                className="text-capitalize"
              >
                {selectedPlan?.planTitle}
              </Typography>
              <Chip
                label="Selected"
                color="primary"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Current vs New Plan comparison */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: alpha(theme.palette.grey[500], 0.04),
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  Current Plan
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  className="text-capitalize"
                  mt={0.5}
                >
                  {currentPlan?.license_plan_name || currentPlan?.license_plan_code || "Current plan"}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.primary.main}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                }}
              >
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  New Plan
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  className="text-capitalize"
                  mt={0.5}
                >
                  {selectedPlan?.planTitle}
                </Typography>
              </Paper>
            </Box>
          </Paper>

          {/* GST Number — compact inline row */}
          <Paper
            variant="outlined"
            sx={{
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {!editingGst ? (
              <>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    GST Number
                  </Typography>
                  {gstNumber ? (
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ letterSpacing: 0.5, fontFamily: "monospace" }}
                    >
                      {gstNumber}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{ cursor: "pointer", fontWeight: 500 }}
                      onClick={() => setEditingGst(true)}
                    >
                      + Add for invoices
                    </Typography>
                  )}
                </Box>
                {gstNumber && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setGstInput(gstNumber);
                      setEditingGst(true);
                    }}
                    sx={{
                      textTransform: "none",
                      minWidth: "auto",
                      fontWeight: 500,
                    }}
                  >
                    Edit
                  </Button>
                )}
              </>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ width: "100%" }}
                flexWrap="wrap"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  GST Number
                </Typography>
                <TextField
                  size="small"
                  placeholder="Enter GST number"
                  value={gstInput}
                  onChange={(e) => setGstInput(e.target.value)}
                  sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveGst}
                  disabled={savingGst}
                  sx={{ textTransform: "none", fontWeight: 600, flexShrink: 0 }}
                >
                  Save
                </Button>
                {gstNumber && (
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    onClick={handleClearGst}
                    disabled={savingGst}
                    sx={{ textTransform: "none", fontWeight: 500 }}
                  >
                    Clear
                  </Button>
                )}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setEditingGst(false);
                    setGstInput(gstNumber);
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Paper>

          {/* Billing Address Card */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <LocationOn sx={{ fontSize: 20, color: "text.secondary" }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Billing Address
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => setEditingAddress(true)}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.5,
                  p: 0.5,
                }}
              >
                <EditOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {loadingAddress ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : billingAddress ? (
              <Box>
                <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                  {billingAddress.line1}
                </Typography>
                {billingAddress.line2 && (
                  <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                    {billingAddress.line2}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {[billingAddress.city, billingAddress.state]
                    .filter(Boolean)
                    .join(", ")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {billingAddress.country}
                  {billingAddress.postal_code
                    ? ` - ${billingAddress.postal_code}`
                    : ""}
                </Typography>
              </Box>
            ) : null}
          </Paper>
        </Grid2>

        {/* Right: Order Summary */}
        <Grid2 size={{ xs: 12, md: 5 }} sx={{ display: "flex" }}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" fontWeight={700} mb={2}>
              Order Summary
            </Typography>

            {loadingPreview ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Plan
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    className="text-capitalize"
                  >
                    {selectedPlan?.planTitle}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Billing Cycle
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    Monthly
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Base Price
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {currency} {basePrice}
                  </Typography>
                </Box>

                {taxPercent > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1.5}>
                    <Typography variant="body2" color="text.secondary">
                      Tax ({taxPercent}%)
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {currency} {taxAmount}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Plan Total
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {currency} {newPlanAmount}
                  </Typography>
                </Box>

                {/* Prorated credit - only shown for upgrades */}
                {isUpgrade && proratedCredit && Number(proratedCredit) > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1.5}>
                    <Typography variant="body2" color="text.secondary">
                      Current plan credit
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="success.main"
                    >
                      − {currency} {proratedCredit}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="baseline"
                  mb={0.5}
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    {isUpgrade ? "Amount Due Today" : "Total"}
                  </Typography>
                  <Typography variant="h5" color="primary" fontWeight={800}>
                    {currency} {amountToPay}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {isUpgrade
                    ? `Auto-renewal: ${currency} ${newPlanAmount}/month from next cycle`
                    : "Auto-renewal enabled. Cancel anytime."}
                </Typography>
              </>
            )}

            {/* Pay button + security note — pinned to bottom of card */}
            <Box sx={{ mt: "auto", pt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={loadingPreview}
                onClick={() =>
                  onProceedToPayment(
                    selectedPlan,
                    paymentPreview,
                    billingAddress,
                  )
                }
                sx={{
                  width: "100%",
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  mb: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                  whiteSpace: "nowrap",
                }}
              >
                {`Pay ${currency} ${amountToPay}`}
              </Button>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.5}
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
                <Typography variant="caption" color="text.secondary">
                  Secured payment. Your data is encrypted.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export const PaymentSuccessDialog = ({
  selectedPlan,
  paymentId,
  onClose,
}: any) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      {/* Success Icon */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          bgcolor: alpha(theme.palette.success.main, 0.1),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 48, color: "success.main" }} />
      </Box>

      <Typography variant="h5" fontWeight={700} mb={1}>
        Payment Successful!
      </Typography>

      <Typography variant="body1" color="text.secondary" mb={3}>
        Your subscription has been upgraded successfully.
      </Typography>

      {/* Plan upgrade card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.success.light}`,
          bgcolor: alpha(theme.palette.success.main, 0.04),
          width: "100%",
          maxWidth: 400,
          mb: 3,
        }}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: 1 }}
        >
          Your New Plan
        </Typography>
        <Typography
          variant="h4"
          fontWeight={800}
          color="success.main"
          className="text-capitalize"
          sx={{ mt: 0.5, mb: 1 }}
        >
          {selectedPlan?.planTitle}
        </Typography>
        <Chip
          label="Active"
          color="success"
          size="small"
          sx={{ fontWeight: 600 }}
        />
      </Paper>

      {/* Transaction details */}
      {paymentId && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            width: "100%",
            maxWidth: 400,
            mb: 3,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Transaction ID
            </Typography>
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ fontFamily: "monospace" }}
            >
              {paymentId}
            </Typography>
          </Box>
        </Paper>
      )}

      <Typography
        variant="body2"
        color="text.secondary"
        mb={3}
        sx={{ maxWidth: 400 }}
      >
        Your plan features are now active. You can view your updated usage
        limits and billing details from the subscription page.
      </Typography>

      <Button
        variant="contained"
        color="primary"
        onClick={onClose}
        sx={{
          px: 5,
          py: 1.2,
          fontWeight: 600,
          borderRadius: 2,
          textTransform: "none",
          fontSize: "0.95rem",
        }}
      >
        Done
      </Button>
    </Box>
  );
};

export const DowngradeConfirmationDialog = ({
  selectedPlan,
  currentPlan,
  effectiveDate,
  onConfirm,
  onBack,
  isSubmitting,
}: any) => {
  const theme = useTheme();

  const formatDate = (value: any) => {
    if (!value) return "end of current billing cycle";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "end of current billing cycle";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          variant="text"
          size="small"
          onClick={onBack}
          sx={{ textTransform: "none", fontWeight: 500, mr: 1 }}
        >
          ← Back to Plans
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            mb: 3,
          }}
        >
          <InfoOutlinedIcon color="warning" />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              Your current plan remains active until {formatDate(effectiveDate)}
              .
            </Typography>
            <Typography variant="body2" color="text.secondary">
              On the next billing cycle, auto-pay will charge the new plan's
              amount and switch you to the lower tier.
            </Typography>
          </Box>
        </Stack>

        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.grey[500], 0.04),
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Current Plan
              </Typography>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                className="text-capitalize"
                mt={0.5}
              >
                {currentPlan?.license_plan_name || "Current"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active until {formatDate(effectiveDate)}
              </Typography>
            </Paper>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `2px solid ${theme.palette.warning.main}`,
                bgcolor: alpha(theme.palette.warning.main, 0.04),
              }}
            >
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                New Plan
              </Typography>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                className="text-capitalize"
                mt={0.5}
              >
                {selectedPlan?.planTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPlan?.currency} {selectedPlan?.price} / cycle
              </Typography>
            </Paper>
          </Grid2>
        </Grid2>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary" mb={2}>
          By confirming, your subscription will be scheduled to move to{" "}
          <b className="text-capitalize">{selectedPlan?.planTitle}</b> on{" "}
          <b>{formatDate(effectiveDate)}</b>. You will continue to have full
          access to your current plan's features until that date.
        </Typography>

        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <Button
            variant="outlined"
            onClick={onBack}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={onConfirm}
            disabled={isSubmitting}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              px: 4,
            }}
          >
            {isSubmitting ? "Scheduling..." : "Confirm Downgrade"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export const DowngradeRetainSelectionDialog = ({
  selectedPlan,
  effectiveDate,
  channelLimits,
  preloadedChannels = null,
  showChannels = true,
  onBack,
  onConfirm,
  isSubmitting,
}: any) => {
  const theme = useTheme();
  const apiService = useApiService();

  const [channels, setChannels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedChannelIds, setSelectedChannelIds] = useState<
    Record<string, boolean>
  >({});

  const channelIdOf = (c: any) => c?.id || c?._id;
  const channelLabel = (c: any) => c?.channel_name || c?.title || "Channel";

  const formatDate = (value: any) => {
    if (!value) return "end of current billing cycle";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "end of current billing cycle";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        let fetchedChannels: any[];

        if (preloadedChannels !== null && preloadedChannels !== undefined) {
          fetchedChannels = preloadedChannels;
        } else {
          const channelTypes = ["targeted", "secure", "broadcast", "group", "shared-object"];
          const channelResults = await Promise.all(channelTypes.map(type => apiService.get(`/channels/${type}?limit=500`)));
          if (cancelled) return;
          fetchedChannels = channelResults.flatMap(res =>
            res?.data?.data?.channels ?? res?.data?.data ?? []
          );
        }

        if (cancelled) return;
        setChannels(fetchedChannels);

        const initialChannelIds: Record<string, boolean> = {};
        if (!showChannels) {
          // All channel types within limit — auto-select everything
          fetchedChannels.forEach((c: any) => {
            const id = c?.id || c?._id;
            if (id) initialChannelIds[id] = true;
          });
        } else {
          // Some types exceed limit — auto-select only the types that are within limit
          const limitsMap = (channelLimits as Record<string, number>) ?? {};
          const typeCountMap: Record<string, number> = {};
          fetchedChannels.forEach((c: any) => {
            const t = c?.type_of_channel;
            if (t) typeCountMap[t] = (typeCountMap[t] ?? 0) + 1;
          });
          fetchedChannels.forEach((c: any) => {
            const id = c?.id || c?._id;
            if (!id) return;
            const type = c?.type_of_channel;
            const limit = limitsMap[type];
            const count = typeCountMap[type] ?? 0;
            if (limit === undefined || limit < 0 || count <= limit) {
              initialChannelIds[id] = true;
            }
          });
        }
        setSelectedChannelIds(initialChannelIds);
      } catch (err: any) {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.message ||
          "Failed to load channels. Please try again.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    apiService,
    preloadedChannels,
    showChannels,
  ]);

  const toggleChannel = (id: string) => {
    setSelectedChannelIds((prev) => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = true;
      }
      return next;
    });
  };

  const CHANNEL_TYPE_LABELS: Record<string, string> = {
    broadcast: "Broadcast Channels",
    group: "Group Channels",
    targeted: "Targeted Channels",
    secure: "Encrypted Channels",
    "shared-object": "Shared Object Channels",
  };

  // Group fetched channels by their type_of_channel field
  const channelsByType = channels.reduce<Record<string, any[]>>((acc, c) => {
    const t = c?.type_of_channel ?? "unknown";
    if (!acc[t]) acc[t] = [];
    acc[t].push(c);
    return acc;
  }, {});

  const selectedCountByType = (type: string) =>
    (channelsByType[type] ?? []).filter((c: any) => {
      const id = channelIdOf(c);
      return id && !!selectedChannelIds[id];
    }).length;

  const anyChannelTypeOverLimit = Object.entries(
    (channelLimits as Record<string, number>) ?? {},
  ).some(([type, limit]) => limit >= 0 && selectedCountByType(type) > limit);

  const confirmDisabled =
    isSubmitting ||
    loading ||
    !!loadError ||
    anyChannelTypeOverLimit;

  const handleConfirm = () => {
    if (confirmDisabled) return;
    onConfirm?.(
      Object.keys(selectedChannelIds),
    );
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2}>
        <Button
          variant="text"
          size="small"
          onClick={onBack}
          sx={{ textTransform: "none", fontWeight: 500, mr: 1 }}
          disabled={isSubmitting}
        >
          ← Back
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            mb: 3,
          }}
        >
          <InfoOutlinedIcon color="warning" />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              Pick the channels to keep after{" "}
              {formatDate(effectiveDate)}.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              On switching to{" "}
              <b className="text-capitalize">{selectedPlan?.planTitle}</b>,
              channels not selected here will be archived. Projects keep their platform limits. Your current plan
              stays active until the cycle ends.
            </Typography>
          </Box>
        </Stack>

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && loadError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {loadError}
          </Alert>
        )}

        {!loading && !loadError && (
          <>
            {showChannels && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                  Channels
                </Typography>
                {channels.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No channels to retain.
                  </Typography>
                ) : (
                  <Grid2 container spacing={2}>
                    {Object.entries(
                      (channelLimits as Record<string, number>) ?? {},
                    ).map(([type, limit]) => {
                      const typeChannels = channelsByType[type] ?? [];
                      if (typeChannels.length === 0) return null;
                      // Within limit — already auto-selected, no need to show
                      if (limit < 0 || typeChannels.length <= limit) return null;
                      const selCount = selectedCountByType(type);
                      const typeOverLimit = selCount > limit;
                      const label =
                        CHANNEL_TYPE_LABELS[type] ?? `${type} Channels`;
                      return (
                        <Grid2 key={type} size={6}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={0.75}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color="text.secondary"
                            >
                              {label}
                            </Typography>
                            <Chip
                              size="small"
                              color={typeOverLimit ? "error" : "default"}
                              label={`${selCount} / ${limit}`}
                            />
                          </Stack>
                          <Paper
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              maxHeight: 180,
                              overflowY: "auto",
                            }}
                          >
                            {typeChannels.map((c: any) => {
                              const id = channelIdOf(c);
                              if (!id) return null;
                              const checked = !!selectedChannelIds[id];
                              const disableThis = !checked && selCount >= limit;
                              return (
                                <FormControlLabel
                                  key={id}
                                  sx={{
                                    display: "flex",
                                    m: 0,
                                    px: 1.5,
                                    py: 0.75,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    "&:last-of-type": { borderBottom: "none" },
                                  }}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={checked}
                                      onChange={() => toggleChannel(id)}
                                      disabled={disableThis || isSubmitting}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                    >
                                      {channelLabel(c)}
                                    </Typography>
                                  }
                                />
                              );
                            })}
                          </Paper>
                        </Grid2>
                      );
                    })}
                  </Grid2>
                )}
              </Box>
            )}
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={isSubmitting}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              px: 4,
            }}
          >
            {isSubmitting ? "Scheduling..." : "Confirm Downgrade"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export const UpgradeRestoreSelectionDialog = ({
  restoreData,
  onConfirm,
  onSkip,
  isSubmitting,
}: any) => {
  const theme = useTheme();

  const projects: any[] = restoreData?.projects ?? [];
  const channels: any[] = restoreData?.channels ?? [];
  const projectLimit = restoreData?.project_limit;
  const channelLimits: Record<
    string,
    { limit: number; current_active: number; headroom: number }
  > = restoreData?.channel_limits ?? {};

  const projectHeadroom: number | undefined = projectLimit?.headroom === -1 ? undefined : projectLimit?.headroom;

  const [selectedProjectIds, setSelectedProjectIds] = useState<
    Record<string, boolean>
  >({});
  const [selectedChannelIds, setSelectedChannelIds] = useState<
    Record<string, boolean>
  >({});

  const CHANNEL_TYPE_LABELS: Record<string, string> = {
    broadcast: "Broadcast Channels",
    group: "Group Channels",
    targeted: "Targeted Channels",
    secure: "Encrypted Channels",
    "shared-object": "Shared Object Channels",
  };

  const channelsByType = channels.reduce<Record<string, any[]>>(
    (acc: Record<string, any[]>, c: any) => {
      const t = c?.type_of_channel ?? "unknown";
      if (!acc[t]) acc[t] = [];
      acc[t].push(c);
      return acc;
    },
    {},
  );

  // Pre-select up to headroom for each feature. If headroom isn't known
  // (plan lookup failed server-side), pre-select all and let the POST
  // validate. Pre-selection is oldest-first by created_at when available,
  // otherwise the list's natural order — matches the user's mental model
  // of "give my older stuff back first."
  useEffect(() => {
    const initialProjects: Record<string, boolean> = {};
    const sortedProjects = [...projects].sort((a: any, b: any) => {
      const at = new Date(a?.created_at ?? 0).getTime();
      const bt = new Date(b?.created_at ?? 0).getTime();
      return at - bt;
    });
    const projectCap =
      typeof projectHeadroom === "number"
        ? projectHeadroom
        : sortedProjects.length;
    sortedProjects.slice(0, projectCap).forEach((p: any) => {
      if (p?.id) initialProjects[p.id] = true;
    });
    setSelectedProjectIds(initialProjects);

    const initialChannels: Record<string, boolean> = {};
    Object.entries(channelsByType).forEach(([type, typeChannels]) => {
      const sorted = [...(typeChannels as any[])].sort(
        (a: any, b: any) =>
          new Date(a?.created_at ?? 0).getTime() -
          new Date(b?.created_at ?? 0).getTime(),
      );
      const cap =
        typeof channelLimits[type]?.headroom === "number"
          ? channelLimits[type].headroom
          : sorted.length;
      sorted.slice(0, cap).forEach((c: any) => {
        if (c?.id) initialChannels[c.id] = true;
      });
    });
    setSelectedChannelIds(initialChannels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreData]);

  const selectedProjectCount = Object.keys(selectedProjectIds).length;
  const projectOverLimit =
    typeof projectHeadroom === "number" &&
    selectedProjectCount > projectHeadroom;

  const selectedCountForChannelType = (type: string) =>
    (channelsByType[type] ?? []).filter(
      (c: any) => c?.id && !!selectedChannelIds[c.id],
    ).length;

  const anyChannelTypeOverLimit = Object.keys(channelsByType).some((type) => {
    const headroom = channelLimits[type]?.headroom;
    if (typeof headroom !== "number") return false;
    return selectedCountForChannelType(type) > headroom;
  });

  const toggleProject = (id: string) => {
    setSelectedProjectIds((prev) => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        // Cap project additions at headroom
        if (
          typeof projectHeadroom === "number" &&
          Object.keys(prev).length >= projectHeadroom
        ) {
          return prev;
        }
        next[id] = true;
      }
      return next;
    });
  };

  const toggleChannel = (id: string, type: string) => {
    setSelectedChannelIds((prev) => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        // Cap channel additions at per-type headroom
        const headroom = channelLimits[type]?.headroom;
        if (typeof headroom === "number") {
          const current = (channelsByType[type] ?? []).filter(
            (c: any) => c?.id && !!prev[c.id],
          ).length;
          if (current >= headroom) return prev;
        }
        next[id] = true;
      }
      return next;
    });
  };

  const setProjectGroup = (selectAll: boolean) => {
    if (selectAll) {
      const next: Record<string, boolean> = {};
      const sorted = [...projects].sort((a: any, b: any) => {
        const at = new Date(a?.created_at ?? 0).getTime();
        const bt = new Date(b?.created_at ?? 0).getTime();
        return at - bt;
      });
      const cap =
        typeof projectHeadroom === "number" ? projectHeadroom : sorted.length;
      sorted.slice(0, cap).forEach((p: any) => {
        if (p?.id) next[p.id] = true;
      });
      setSelectedProjectIds(next);
    } else {
      setSelectedProjectIds({});
    }
  };

  const setChannelGroup = (type: string, selectAll: boolean) => {
    setSelectedChannelIds((prev) => {
      const next = { ...prev };
      const items = channelsByType[type] ?? [];
      if (selectAll) {
        const sorted = [...items].sort(
          (a: any, b: any) =>
            new Date(a?.created_at ?? 0).getTime() -
            new Date(b?.created_at ?? 0).getTime(),
        );
        const cap =
          typeof channelLimits[type]?.headroom === "number"
            ? channelLimits[type].headroom
            : sorted.length;
        sorted.slice(0, cap).forEach((c: any) => {
          if (c?.id) next[c.id] = true;
        });
      } else {
        items.forEach((c: any) => {
          if (c?.id) delete next[c.id];
        });
      }
      return next;
    });
  };

  const selectedChannelCount = Object.keys(selectedChannelIds).length;
  const nothingSelected =
    selectedProjectCount === 0 && selectedChannelCount === 0;

  const handleConfirm = () => {
    if (
      isSubmitting ||
      nothingSelected ||
      projectOverLimit ||
      anyChannelTypeOverLimit
    )
      return;
    onConfirm?.(
      Object.keys(selectedProjectIds),
      Object.keys(selectedChannelIds),
    );
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.success.main, 0.08),
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            mb: 3,
          }}
        >
          <InfoOutlinedIcon color="success" />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              Welcome back — pick what to restore
            </Typography>
            <Typography variant="body2" color="text.secondary">
              These projects and channels were locked when you downgraded. Pick
              which ones to bring back now. We've pre-selected the oldest up to
              the available capacity. Projects use platform limits; channels use app limits. Anything you skip
              stays locked and can be restored later.
            </Typography>
          </Box>
        </Stack>

        {projects.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Projects
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setProjectGroup(true)}
                  disabled={isSubmitting}
                  sx={{ textTransform: "none", minWidth: 0, px: 0.5 }}
                >
                  Select all
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setProjectGroup(false)}
                  disabled={isSubmitting}
                  sx={{ textTransform: "none", minWidth: 0, px: 0.5 }}
                >
                  Clear
                </Button>
              </Stack>
              <Chip
                size="small"
                color={projectOverLimit ? "error" : "default"}
                label={
                  typeof projectHeadroom === "number"
                    ? `${selectedProjectCount} / ${projectHeadroom} can be restored`
                    : `${selectedProjectCount} selected`
                }
              />
            </Stack>
            {typeof projectHeadroom === "number" && projectLimit && (
              <Typography
                variant="caption"
                color="text.secondary"
                mb={1}
                display="block"
              >
                Platform limit {projectLimit.limit === -1 ? "Unlimited" : projectLimit.limit} • currently active{" "}
                {projectLimit.current_active}
              </Typography>
            )}
            <Paper
              variant="outlined"
              sx={{ borderRadius: 2, maxHeight: 260, overflowY: "auto" }}
            >
              {projects.map((p: any) => {
                if (!p?.id) return null;
                const checked = !!selectedProjectIds[p.id];
                const atProjectCap =
                  typeof projectHeadroom === "number" &&
                  !checked &&
                  selectedProjectCount >= projectHeadroom;
                return (
                  <FormControlLabel
                    key={p.id}
                    sx={{
                      display: "flex",
                      m: 0,
                      px: 2,
                      py: 1,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={() => toggleProject(p.id)}
                        disabled={isSubmitting || atProjectCap}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {p.title || p.project_key || "Project"}
                        </Typography>
                        {p.project_key && (
                          <Typography variant="caption" color="text.secondary">
                            {p.project_key}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </Paper>
          </Box>
        )}

        {channels.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
              Channels
            </Typography>
            <Grid2 container spacing={2}>
              {Object.entries(channelsByType).map(([type, typeChannels]) => {
                const label = CHANNEL_TYPE_LABELS[type] ?? `${type} Channels`;
                const selCount = selectedCountForChannelType(type);
                const headroom = channelLimits[type]?.headroom;
                const limit = channelLimits[type]?.limit;
                const currentActive = channelLimits[type]?.current_active;
                const overLimit =
                  typeof headroom === "number" && selCount > headroom;
                return (
                  <Grid2 key={type} size={6}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={0.75}
                    >
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="text.secondary"
                        >
                          {label}
                        </Typography>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setChannelGroup(type, true)}
                          disabled={isSubmitting}
                          sx={{
                            textTransform: "none",
                            minWidth: 0,
                            px: 0.5,
                            fontSize: 11,
                          }}
                        >
                          All
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setChannelGroup(type, false)}
                          disabled={isSubmitting}
                          sx={{
                            textTransform: "none",
                            minWidth: 0,
                            px: 0.5,
                            fontSize: 11,
                          }}
                        >
                          Clear
                        </Button>
                      </Stack>
                      <Chip
                        size="small"
                        color={overLimit ? "error" : "default"}
                        label={
                          typeof headroom === "number"
                            ? `${selCount} / ${headroom}`
                            : `${selCount}`
                        }
                      />
                    </Stack>
                    {typeof headroom === "number" && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        mb={0.5}
                        display="block"
                      >
                        Plan limit {limit} • active {currentActive}
                      </Typography>
                    )}
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        maxHeight: 180,
                        overflowY: "auto",
                      }}
                    >
                      {(typeChannels as any[]).map((c: any) => {
                        if (!c?.id) return null;
                        const checked = !!selectedChannelIds[c.id];
                        const atTypeCap =
                          typeof headroom === "number" &&
                          !checked &&
                          selCount >= headroom;
                        return (
                          <FormControlLabel
                            key={c.id}
                            sx={{
                              display: "flex",
                              m: 0,
                              px: 1.5,
                              py: 0.75,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              "&:last-of-type": { borderBottom: "none" },
                            }}
                            control={
                              <Checkbox
                                size="small"
                                checked={checked}
                                onChange={() => toggleChannel(c.id, type)}
                                disabled={isSubmitting || atTypeCap}
                              />
                            }
                            label={
                              <Typography variant="body2" fontWeight={600}>
                                {c.channel_name || "Channel"}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </Paper>
                  </Grid2>
                );
              })}
            </Grid2>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <Button
            variant="outlined"
            onClick={onSkip}
            disabled={isSubmitting}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Skip for now
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirm}
            disabled={
              isSubmitting ||
              nothingSelected ||
              projectOverLimit ||
              anyChannelTypeOverLimit
            }
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              px: 4,
            }}
          >
            {isSubmitting
              ? "Restoring..."
              : `Restore ${selectedProjectCount + selectedChannelCount} item${selectedProjectCount + selectedChannelCount === 1 ? "" : "s"
              }`}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export const DowngradeScheduledDialog = ({
  selectedPlan,
  effectiveDate,
  onClose,
}: any) => {
  const theme = useTheme();

  const formatDate = (value: any) => {
    if (!value) return "the end of your current billing cycle";
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
      return "the end of your current billing cycle";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          bgcolor: alpha(theme.palette.warning.main, 0.1),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 48, color: "warning.main" }} />
      </Box>

      <Typography variant="h5" fontWeight={700} mb={1}>
        Downgrade Scheduled
      </Typography>

      <Typography variant="body1" color="text.secondary" mb={3}>
        Your subscription will move to{" "}
        <b className="text-capitalize">{selectedPlan?.planTitle}</b> on{" "}
        <b>{formatDate(effectiveDate)}</b>.
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        mb={3}
        sx={{ maxWidth: 420 }}
      >
        You'll keep your current plan's features until then. On the next billing
        cycle, auto-pay will charge the new plan amount.
      </Typography>

      <Button
        variant="contained"
        color="primary"
        onClick={onClose}
        sx={{
          px: 5,
          py: 1.2,
          fontWeight: 600,
          borderRadius: 2,
          textTransform: "none",
          fontSize: "0.95rem",
        }}
      >
        Done
      </Button>
    </Box>
  );
};

interface PaymentFailureDialogProps {
  open: boolean;
  onClose: () => void;
  onRetry: () => void;
  onChangePlan: () => void;
  onUpdatePaymentMethod?: () => void;
  currentPlan?: {
    license_plan_name?: string;
    license_plan_code?: string;
  } | null;
  failedPlanTitle?: string;
  failedPlanType?: string;
  failedPlanPrice?: number;
  /** Raw billing-schedule status from the API (e.g. "failed", "active"). */
  status?: string;
  failureReason?: string;
  failedAmount?: number;
  failedCurrency?: string;
  graceDaysLeft?: number;
  graceEndsAt?: string;
  failureAttemptedAt?: string;
  isHardBlocking?: boolean;
  isProcessing?: boolean;
}



export const PaymentFailureDialog = ({
  open,
  onClose,
  onRetry,
  onChangePlan,
  onUpdatePaymentMethod,
  currentPlan,
  failedPlanTitle,
  failedPlanType,
  failedPlanPrice,
  status,
  failureReason,
  failedAmount,
  failedCurrency,
  graceDaysLeft,
  graceEndsAt,
  failureAttemptedAt,
  isHardBlocking,
  isProcessing,
}: PaymentFailureDialogProps) => {
  const planLabel =
    failedPlanTitle ||
    currentPlan?.license_plan_name ||
    currentPlan?.license_plan_code ||
    "your plan";

  const formatDateTime = (value?: string) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatDate = (value?: string) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const attemptedAtLabel = formatDateTime(failureAttemptedAt);
  const graceEndsLabel = formatDate(graceEndsAt);

  const hasGraceInfo = typeof graceDaysLeft === "number";
  const isFinalDay = hasGraceInfo && (graceDaysLeft as number) <= 1;

  const effectiveAmount =
    typeof failedAmount === "number" ? failedAmount : failedPlanPrice;
  const effectiveCurrency = failedCurrency || "";
  const amountLine =
    typeof effectiveAmount === "number"
      ? effectiveAmount === 0
        ? "Free"
        : `${effectiveCurrency} ${effectiveAmount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`.trim()
      : null;





  const parseApiTimestamp = (value: string): number => {
    const normalized = value.replace(
      /(\.\d{3})\d+(?=[+\-Z]|$)/,
      "$1",
    );
    return new Date(normalized).getTime();
  };

  const graceProgress = (() => {
    if (!failureAttemptedAt || !graceEndsAt) return null;
    const startMs = parseApiTimestamp(failureAttemptedAt);
    const endMs = parseApiTimestamp(graceEndsAt);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
    const totalMs = endMs - startMs;
    if (totalMs <= 0) return null;

    const nowMs = Date.now();
    const elapsedMs = Math.max(0, nowMs - startMs);
    const pct = Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100));

    const msPerDay = 1000 * 60 * 60 * 24;
    const totalDays = Math.max(1, Math.round(totalMs / msPerDay));
    const elapsedDays = Math.min(
      totalDays,
      Math.max(0, Math.floor(elapsedMs / msPerDay)),
    );
    return { elapsedDays, totalDays, pct };
  })();


  const getFailureDisplayText = (reason?: string): string => {
    if (!reason) return "";
    const r = reason.toLowerCase();
    if (/token/.test(r)) return "Couldn't save your card for autopay";
    if (/insufficient|nsf/.test(r)) return "Insufficient funds";
    if (/decline/.test(r)) return "Card declined by bank";
    if (/expired/.test(r)) return "Card expired";
    if (/authentication|3ds|otp/.test(r))
      return "Bank authentication required";
    if (/network|timeout|gateway/.test(r))
      return "Payment gateway timeout";
    return reason;
  };

  const failureDisplayText = getFailureDisplayText(failureReason);

  const possibleReasons = (() => {
    const r = (failureReason || "").toLowerCase();
    if (/token/.test(r))
      return [
        "Card expired",
        "Bank blocked recurring payments",
        "Card removed from bank",
        "Network issue during card save",
      ];
    if (/decline|insufficient|nsf/.test(r))
      return [
        "Insufficient funds at the time of charge",
        "Bank flagged the transaction as suspicious",
        "Daily transaction limit reached",
        "Card needs to be reactivated by the issuer",
      ];
    if (/expired/.test(r))
      return [
        "Card expiration date has passed",
        "Bank issued a replacement card",
        "Update the card on file with new details",
      ];
    if (/authentication|3ds|otp/.test(r))
      return [
        "OTP was not entered in time",
        "Bank requires 3D Secure verification",
        "Authentication was cancelled mid-flow",
      ];
    return null;
  })();

  return (
    <ArtDialog
      open={open}
      onClose={isHardBlocking ? () => { } : onClose}
      title="Payment Failed"
      size="large"
      dataQa="payment-failure-dialog"
      actions={
        <Box className={paymentFailureStyles.dialog_actions}>
          {!isHardBlocking && (
            <ArtButton
              label="Remind Me Later"
              variant="outlined"
              color="inherit"
              onClick={onClose}
              disabled={isProcessing}
              dataQa="btn-payment-failure-dismiss"
            />
          )}
          <Box className={paymentFailureStyles.dialog_actions_right}>
            <ArtButton
              label="Change Plan"
              variant="outlined"
              color="primary"
              onClick={onChangePlan}
              disabled={isProcessing}
              dataQa="btn-payment-failure-change-plan"
            />
            <ArtButton
              label={isProcessing ? "Processing..." : "Retry Payment"}
              variant="outlined"
              color="primary"
              onClick={onRetry}
              disabled={isProcessing}
              dataQa="btn-payment-failure-retry"
            />
            {onUpdatePaymentMethod && (
              <ArtButton
                label="Update Payment Method"
                variant="contained"
                color="primary"
                onClick={onUpdatePaymentMethod}
                disabled={isProcessing}
                dataQa="btn-payment-failure-update-method"
              />
            )}
          </Box>
        </Box>
      }
    >
      <Box>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 7 }}>
            <Box className={paymentFailureStyles.hero}>
              <Box className={paymentFailureStyles.hero_icon}>
                <ErrorOutlineIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  className={paymentFailureStyles.hero_title}
                >
                  Your <span className="text-capitalize">{planLabel}</span>{" "}
                  subscription is still active.
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className={paymentFailureStyles.hero_subtitle}
                >
                  We couldn't process your latest payment.
                </Typography>
                {graceEndsLabel && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className={paymentFailureStyles.hero_subtitle}
                  >
                    Please update your payment method before{" "}
                    <b className={paymentFailureStyles.hero_deadline_date}>
                      {graceEndsLabel}
                    </b>{" "}
                    to avoid service interruption.
                  </Typography>
                )}
              </Box>
            </Box>

            {hasGraceInfo && (
              <Box className={paymentFailureStyles.grace_card}>
                <Box className={paymentFailureStyles.grace_header}>
                  <Box className={paymentFailureStyles.grace_icon}>
                    <AccessTimeIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box flexGrow={1}>
                    <Typography
                      variant="caption"
                      className={paymentFailureStyles.grace_label}
                    >
                      Grace Period
                    </Typography>
                    <Typography
                      variant="h6"
                      className={paymentFailureStyles.grace_days}
                    >
                      {graceDaysLeft}{" "}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        fontWeight={400}
                      >
                        {(graceDaysLeft as number) === 1
                          ? "day remaining"
                          : "days remaining"}
                      </Typography>
                    </Typography>
                    {graceEndsLabel && (
                      <Typography variant="caption" color="text.secondary">
                        Ends {graceEndsLabel}
                      </Typography>
                    )}
                  </Box>
                  {graceProgress && (
                    <Typography
                      variant="caption"
                      className={paymentFailureStyles.grace_total}
                    >
                      {graceProgress.totalDays} days
                    </Typography>
                  )}
                </Box>
                {graceProgress && (
                  <>
                    <Box
                      className={paymentFailureStyles.grace_progress}
                      role="progressbar"
                      aria-valuenow={Math.round(graceProgress.pct)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <Box
                        className={paymentFailureStyles.grace_progress_bar}
                        style={{ width: `${graceProgress.pct}%` }}
                      />
                    </Box>
                    <Box
                      className={paymentFailureStyles.grace_progress_legend}
                    >
                      <Typography variant="caption" color="text.secondary">
                        0
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {graceProgress.totalDays}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}

            {(failureReason || possibleReasons) && (
              <Grid2 container spacing={3}>
                {failureReason && (
                  <Grid2 size={{ xs: 12, sm: possibleReasons ? 6 : 12 }}>
                    <Box className={paymentFailureStyles.section_header}>
                      <CreditCard sx={{ fontSize: 18, color: "error.main" }} />
                      <Typography
                        variant="subtitle2"
                        className={paymentFailureStyles.section_title}
                      >
                        Why did this happen?
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      className={paymentFailureStyles.explanation_headline}
                    >
                      We couldn't save your card for recurring billing.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mt: 1 }}>
                      {failureDisplayText}
                    </Typography>
                  </Grid2>
                )}
                {possibleReasons && (
                  <Grid2 size={{ xs: 12, sm: failureReason ? 6 : 12 }}>
                    <Box className={paymentFailureStyles.section_header}>
                      <HelpOutlineIcon
                        sx={{ fontSize: 18, color: "info.main" }}
                      />
                      <Typography
                        variant="subtitle2"
                        className={paymentFailureStyles.section_title}
                      >
                        Possible reasons
                      </Typography>
                    </Box>
                    <Box
                      component="ul"
                      className={paymentFailureStyles.reasons_list}
                    >
                      {possibleReasons.map((r) => (
                        <li key={r}>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                          >
                            {r}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  </Grid2>
                )}
              </Grid2>
            )}
          </Grid2>

          <Grid2
            size={{ xs: 12, md: 5 }}
            className={paymentFailureStyles.right_column}
          >
            <Box className={paymentFailureStyles.summary_card}>
              <Box className={paymentFailureStyles.summary_header}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Plan
                  </Typography>
                  <Box className={paymentFailureStyles.summary_plan_row}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      className="text-capitalize"
                    >
                      {planLabel}
                    </Typography>
                    {failedPlanType && (
                      <Chip
                        label={failedPlanType}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                </Box>
                {amountLine && (
                  <Box className={paymentFailureStyles.summary_amount}>
                    <Typography variant="caption" color="text.secondary">
                      Amount Due
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      className={paymentFailureStyles.summary_amount_value}
                    >
                      {amountLine}
                    </Typography>
                    {typeof effectiveAmount === "number" &&
                      effectiveAmount > 0 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          per billing cycle
                        </Typography>
                      )}
                  </Box>
                )}
              </Box>

              <Box className={paymentFailureStyles.summary_divider} />

              {/* Icon rows */}
              {attemptedAtLabel && (
                <Box className={paymentFailureStyles.summary_row}>
                  <Box className={paymentFailureStyles.summary_row_label}>
                    <CalendarTodayIcon
                      className={paymentFailureStyles.summary_row_icon}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Last Attempt
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={500}>
                    {attemptedAtLabel}
                  </Typography>
                </Box>
              )}

              {graceEndsLabel && (
                <Box className={paymentFailureStyles.summary_row}>
                  <Box className={paymentFailureStyles.summary_row_label}>
                    <AccessTimeIcon
                      className={paymentFailureStyles.summary_row_icon_orange}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Grace Period Ends
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="body2" fontWeight={500}>
                      {graceEndsLabel}
                    </Typography>
                    {typeof graceDaysLeft === "number" && graceDaysLeft > 0 && (
                      <Typography
                        variant="caption"
                        className={
                          paymentFailureStyles.summary_grace_days_left
                        }
                      >
                        ({graceDaysLeft} days left)
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {typeof status === "string" && status.length > 0 && (
                <Box className={paymentFailureStyles.summary_row}>
                  <Box className={paymentFailureStyles.summary_row_label}>
                    <ErrorOutlineIcon
                      className={
                        paymentFailureStyles.summary_row_icon_status
                      }
                    />
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                  </Box>
                  <Chip
                    label={status.charAt(0).toUpperCase() + status.slice(1)}
                    size="small"
                    className={`${paymentFailureStyles.status_chip} ${status.toLowerCase() === "failed"
                        ? paymentFailureStyles.status_chip_failed
                        : status.toLowerCase() === "active"
                          ? paymentFailureStyles.status_chip_active
                          : paymentFailureStyles.status_chip_neutral
                      }`}
                  />
                </Box>
              )}

              {failureReason && (
                <Box className={paymentFailureStyles.summary_row}>
                  <Box className={paymentFailureStyles.summary_row_label}>
                    <WarningAmberRoundedIcon
                      className={
                        paymentFailureStyles.summary_row_icon_status
                      }
                    />
                    <Typography variant="body2" color="text.secondary">
                      Reason
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    className={paymentFailureStyles.summary_reason_value}
                  >
                    {failureDisplayText}
                  </Typography>
                </Box>
              )}

            </Box>
          </Grid2>
        </Grid2>

        <Box className={paymentFailureStyles.next_steps_panel}>
          <Box className={paymentFailureStyles.next_steps_header}>
            <ShieldOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography
              variant="subtitle2"
              className={paymentFailureStyles.section_title}
            >
              What happens next?
            </Typography>
          </Box>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 4 }}>
              <Box className={paymentFailureStyles.next_steps_card}>
                <Box
                  className={`${paymentFailureStyles.next_steps_card_icon} ${paymentFailureStyles.next_steps_card_icon_success}`}
                >
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    className={paymentFailureStyles.next_steps_card_title}
                  >
                    Your subscription remains active
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    You can continue using all{" "}
                    <span className="text-capitalize">{planLabel}</span>{" "}
                    features.
                  </Typography>
                </Box>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 4 }}>
              <Box className={paymentFailureStyles.next_steps_card}>
                <Box
                  className={`${paymentFailureStyles.next_steps_card_icon} ${paymentFailureStyles.next_steps_card_icon_info}`}
                >
                  <AutorenewIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    className={paymentFailureStyles.next_steps_card_title}
                  >
                    We'll retry automatically
                  </Typography>
                </Box>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 4 }}>
              <Box className={paymentFailureStyles.next_steps_card}>
                <Box
                  className={`${paymentFailureStyles.next_steps_card_icon} ${paymentFailureStyles.next_steps_card_icon_orange}`}
                >
                  <CalendarTodayIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    className={paymentFailureStyles.next_steps_card_title}
                  >
                    Avoid service interruption
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Update your payment method
                    {graceEndsLabel ? (
                      <>
                        {" "}
                        before{" "}
                        <b
                          className={paymentFailureStyles.hero_deadline_date}
                        >
                          {graceEndsLabel}
                        </b>
                        .
                      </>
                    ) : (
                      "."
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid2>
          </Grid2>
        </Box>

        {isFinalDay && (
          <Alert severity="error" sx={{ mt: 2 }}>
            This is the last day of your grace period. Pay now or change your
            plan to avoid losing access.
          </Alert>
        )}
      </Box>
    </ArtDialog>
  );
};

export const SupportDialog = ({ currentPlan }: any) => {
  const planName =
    currentPlan?.license_plan_name || currentPlan?.license_plan_code || "Current Plan";
  const normalizedPlanName = /plan$/i.test(planName)
    ? planName
    : `${planName} Plan`;
  const isSandboxPlan = isSandboxLicense(currentPlan);

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
        <InfoOutlinedIcon
          color="info"
          sx={{ mt: "4px", height: 30, width: 30 }}
        />

        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            You’re currently on the {normalizedPlanName}.
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {isSandboxPlan ? (
              <>
                The <span style={{ fontWeight: 800 }}>Sandbox plan</span> is
                free and does not expire, but it comes with usage and feature
                limitations.
              </>
            ) : (
              <>
                You’re currently subscribed to the{" "}
                <span style={{ fontWeight: 800 }}>{planName}</span>, with
                features and limits based on your current subscription.
              </>
            )}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Plan upgrades and downgrades are handled by our team to ensure the
            best configuration for your use case.
          </Typography>
        </Box>
      </Stack>

      {/* Contact Support Box */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "primary.light",
          borderRadius: 2,
          p: 2.5,
          backgroundColor: "#F5FAFF",
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please contact our support team and they’ll assist you with the next
          steps.
        </Typography>

        <Box
          sx={{
            p: 1,
            backgroundColor: "#ffffff",
            border: "1px solid",
            borderColor: "primary.light",
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <MailOutlineIcon color="primary" />
            <Typography
              component="a"
              href="mailto:info@aiotrix.com"
              variant="body1"
              sx={{
                color: "primary.main",
                fontWeight: 500,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              info@aiotrix.com
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export const ManageSubscriptionDialog = ({
  currentPlan,
  handleClose,
  onDowngrade,
}: any) => {
  const [licensePlanfeatures, setLicensePlanFeatures] = useState<any[]>([]);
  const [activeSubscription, setActiveSubscription] = useState<any>(null);
  const [openRulesModal, setOpenRulesModal] = useState(false);
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const initialState = ArtStore.getState();

  const authData = initialState.auth;
  const apiService = useApiService();

  const idpService = useIdpService();

  const fetchPlanFeatures = async (planId: string) => {
    try {
      const response = await idpService.get(
        `super-admin/api/${authData?.tenantName}/license-plan-feature-rules/plan/${planId}`,
      );
      setLicensePlanFeatures(response?.data?.data || []);
    } catch (error) {
      apiService.showSnack("Failed to fetch plan limits", "error");
    }
  };

  useEffect(() => {
    const initializeSubscriptionData = async () => {
      if (!authData?.tenantName) return;

      setActiveSubscription(currentPlan);
      const resolvedPlanId = currentPlan?.license_plan_id;

      if (resolvedPlanId) {
        fetchPlanFeatures(resolvedPlanId);
      } else {
        setLicensePlanFeatures([]);
      }
    };

    initializeSubscriptionData();
  }, [authData?.tenantName, currentPlan?.license_plan_id]);

  const formatDate = (value: any) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const billingCycle =
    activeSubscription?.license_plan?.billing_interval ||
    currentPlan?.billing_interval ||
    "N/A";
  const renewalType = currentPlan?.auto_renew ? "Auto-renew" : "Manual";
  const nextRenewalDate =
    activeSubscription?.expires_at || currentPlan?.expiry_date || null;
  const currentPeriodStart =
    activeSubscription?.starts_at || currentPlan?.starts_at || null;
  const planAmountText =
    isSandboxLicense(currentPlan)
      ? "Free"
      : activeSubscription?.pricing
        ? `${activeSubscription?.pricing?.currency || ""} ${activeSubscription?.pricing?.base_price || 0}`
        : "N/A";
  // const planDescriptionText =
  //   licensePlanfeatures.length > 0
  //     ? `Includes ${licensePlanfeatures.length} configured feature limits. Click the eye icon to view all usage rules.`
  //     : "Plan details and limits are configured for your workspace.";

  const apiServiceInstance = new APIService({
    BASE_URL: ART_URL + "/api",
    ART_STORE: ArtStore,
  });

  const apiservice = useApiService();
  const dispatch = useDispatch();

  const payload = { subscription_id: currentPlan?.subscription_id };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      const response = await apiservice.post(
        `/payment/subscription/cancel`,
        payload,
      );
      apiservice.showSnack(response.data?.message, "success");
      setOpenCancelConfirm(false);
      handleClose();
      apiServiceInstance
        .get("/user/profile")
        .then(({ data: { data } }: any) => {
          dispatch(updateLicenseState(licenseStateFromProfile(data)));
        });
    } catch (err: any) {
      console.error("Error cancelling subscription: ", err);
      apiservice.showSnack(
        err?.response?.data?.message ||
        "Failed to cancel subscription. Please try again.",
        "error",
      );
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Subscription Overview
      </Typography>

      <Divider sx={{ mb: 2, borderColor: "rgba(0, 0, 0, 0.2)" }} />

      <Paper
        variant="outlined"
        sx={{ p: 2.5, mb: 3, backgroundColor: "#fafafa" }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
            gap: 3,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mb: 0.5,
                fontSize: "18px",
                letterSpacing: 0.3,
                fontWeight: 700,
              }}
            >
              Current Plan
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {currentPlan?.license_plan_name || currentPlan?.license_plan_code || "Current plan"}
              </Typography>
              <Chip
                size="small"
                label="ACTIVE"
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "success.main" }}
            >
              {planAmountText}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* BILLING DETAILS */}

      <Paper
        variant="outlined"
        sx={{ p: 2.5, mb: 3, backgroundColor: "#fafafa" }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Billing Details
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 3,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mb: 0.5,
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              Billing Cycle
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {billingCycle}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mb: 0.5,
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              Current Period
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "text.secondary" }}
            >
              {formatDate(currentPeriodStart)}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mb: 0.5,
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              Renewal Type
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {renewalType}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mb: 0.5,
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              Next Renewal Date
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "text.secondary" }}
            >
              {formatDate(nextRenewalDate)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {licensePlanfeatures && licensePlanfeatures.length > 0 && (
        <>
          <Accordion
            defaultExpanded={false}
            elevation={0}
            sx={{
              mb: 3,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              boxShadow: "none",
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                justifyContent: "flex-start",
                "& .MuiAccordionSummary-content": {
                  flexGrow: 0,
                  mr: 1,
                },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  color: "inherit",
                  marginLeft: 0,
                },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Plan Features
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Feature</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        Base Limit
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {licensePlanfeatures.map((feature: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2">
                            {feature?.feature?.name || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {feature?.base_limit || "0"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {feature?.limit_unit || "N/A"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: "background.paper",
          marginBottom: 3,
        }}
      >
        <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Downgrade Subscription
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.6, mb: 2 }}
          >
            You can downgrade your subscription to a lower plan. The current
            subscription will remain active until the end of the billing cycle.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            minWidth: { xs: "100%", sm: "auto" },
          }}
        >
          <ArtButton
            label="Downgrade Subscription"
            variant="outlined"
            color="error"
            size="medium"
            onClick={() => {
              if (onDowngrade) {
                onDowngrade(activeSubscription);
              }
            }}
          ></ArtButton>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: "background.paper",
        }}
      >
        <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Cancel Subscription
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}
          >
            You can cancel your subscription at any time. Access will remain
            active until the end of the current billing period.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            minWidth: { xs: "100%", sm: "auto" },
          }}
        >
          <ArtButton
            label="Cancel Subscription"
            variant="outlined"
            color="error"
            size="medium"
            onClick={() => setOpenCancelConfirm(true)}
          ></ArtButton>
        </Box>
      </Paper>

      {/* <Dialog
        open={openCancelConfirm}
        onClose={() => !isCancelling && setOpenCancelConfirm(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
                flexShrink: 0,
              }}
            >
              <WarningAmberRoundedIcon color="error" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Cancel subscription?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action will end your paid plan at the end of the current
                billing cycle.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.info.main, 0.04),
                borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <InfoOutlinedIcon
                  color="info"
                  fontSize="small"
                  sx={{ mt: 0.3 }}
                />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                    You keep full access until the end of your current cycle
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All your current plan's features, limits, and add-ons remain
                    active during the grace period. You won't be charged again.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.04),
                borderColor: (theme) => alpha(theme.palette.warning.main, 0.3),
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <WarningAmberRoundedIcon
                  color="warning"
                  fontSize="small"
                  sx={{ mt: 0.3 }}
                />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                    After the grace period ends
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Your account will automatically move to the free{" "}
                    <b>Sandbox</b> plan. Features that exceed the Sandbox limits
                    will be restricted. You can re-subscribe anytime to restore
                    access.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Typography variant="caption" color="text.secondary">
              This cancels auto-renewal only — no immediate data loss. You can
              reactivate any time before your grace period ends.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1  , display:'flex' , flexDirection:{xs:"column", sm:"row"}}} >
          <Button
            variant="outlined"
            onClick={() => setOpenCancelConfirm(false)}
            disabled={isCancelling}
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
          >
            Keep Subscription
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelSubscription}
            disabled={isCancelling}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            {isCancelling ? "Cancelling..." : "Cancel Subscription"}
          </Button>
        </DialogActions>
      </Dialog> */}

      <ArtDialog
        open={openCancelConfirm}
        onClose={() => !isCancelling && setOpenCancelConfirm(false)}
        title="Cancel subscription?"
        size="medium"
        actionBuilder={() => (
          <Box
            sx={{
              gap: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setOpenCancelConfirm(false)}
              disabled={isCancelling}
              sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            >
              Keep Subscription
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleCancelSubscription}
              disabled={isCancelling}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {isCancelling ? "Cancelling..." : "Cancel Subscription"}
            </Button>
          </Box>
        )}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
                flexShrink: 0,
              }}
            >
              <WarningAmberRoundedIcon color="error" />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This action will end your paid plan at the end of the current
                billing cycle.
              </Typography>
            </Box>
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.info.main, 0.04),
              borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <InfoOutlinedIcon
                color="info"
                fontSize="small"
                sx={{ mt: 0.3 }}
              />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                  You keep full access until the end of your current cycle
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All your current plan's features, limits, and add-ons remain
                  active during the grace period. You won't be charged again.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.04),
              borderColor: (theme) => alpha(theme.palette.warning.main, 0.3),
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <WarningAmberRoundedIcon
                color="warning"
                fontSize="small"
                sx={{ mt: 0.3 }}
              />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                  After the grace period ends
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Your account will automatically move to the free{" "}
                  <b>Sandbox</b> plan. Features that exceed the Sandbox limits
                  will be restricted. You can re-subscribe anytime to restore
                  access.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Typography variant="caption" color="text.secondary">
            This cancels auto-renewal only — no immediate data loss. You can
            reactivate any time before your grace period ends.
          </Typography>
        </Stack>
      </ArtDialog>

      <Dialog
        open={openRulesModal}
        onClose={() => setOpenRulesModal(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Plan Limits & Rules</DialogTitle>
        <DialogContent dividers>
          {licensePlanfeatures.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No limit rules are available for this plan.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Feature</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Included Limit</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Max Allowed</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Extension Step</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    Extension Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Enforcement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {licensePlanfeatures.map((rule: any, index: number) => (
                  <TableRow key={rule?.id || index}>
                    <TableCell>{rule?.feature?.name || "N/A"}</TableCell>
                    <TableCell>{`${rule?.base_limit ?? 0} ${rule?.limit_unit || ""}`}</TableCell>
                    <TableCell>{`${rule?.max_extension_limit ?? 0} ${rule?.limit_unit || ""}`}</TableCell>
                    <TableCell>{`${rule?.extension_step ?? 0} ${rule?.limit_unit || ""}`}</TableCell>
                    <TableCell>{rule?.extension_price ?? 0}</TableCell>
                    <TableCell>
                      {rule?.is_hard_limit ? "Hard limit" : "Soft limit"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// helpers.ts
export function formatInvoiceDate(dateString?: string) {
  if (!dateString) return "-"; // fallback
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-"; // invalid date check

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
}

export type ChildRef = {
  validate: () => boolean;
};

type InvoiceDialogProps = {
  currentPlan: any;
  setSubscriptionData: (data: any) => void;
  selectedPlan: any;
  setPayableAmount: (amount: number) => void;
  setBillingAddressParent: (addr: any) => void;
};

export const InvoiceDialog = forwardRef<ChildRef, InvoiceDialogProps>(
  (
    {
      currentPlan,
      selectedPlan,
      setPayableAmount,
      setBillingAddressParent,
    }: any,
    ref,
  ) => {
    const apiservice = useApiService();
    const [showFeatures, setShowFeatures] = useState(false);
    const [subscriptionSumamry, setSubscriptionSummary]: any = useState(null);
    const [billingAddress, setBillingAddress] = useState({
      line1: "",
      line2: "",
      city: "",
      state: "",
      postal_code: "",
      country: "India",
    });
    const [errors, setErrors] = useState<any>({});

    const handleAddressChange = (field: string, value: string) => {
      setBillingAddress((prev) => {
        const updated = { ...prev, [field]: value };
        if (setBillingAddressParent) {
          setBillingAddressParent(updated);
        }
        return updated;
      });
      setErrors((prev: any) => ({ ...prev, [field]: "" }));
    };

    const subTotal = subscriptionSumamry?.summary?.subTotal || 0;
    const totalPayable = subTotal + subscriptionSumamry?.summary?.taxAmount;

    const today = new Date();
    const formattedDate = today.toISOString().slice(0, 10).replace(/-/g, "");
    const provisionalInvoiceNumber = `${formattedDate}`;

    const countries = ["India", "USA", "Canada", "Australia"];

    const fetchSubscriptionSummary = async () => {
      try {
        const response = await apiservice.get(
          `/invoice-management/summary?licenseId=${selectedPlan?.id}&country=${billingAddress?.country}`,
        );
        const summary = response.data.data;
        setSubscriptionSummary(summary);
      } catch (error) {
        console.error("Error fetching available plans:", error);
      }
    };

    useEffect(() => {
      fetchSubscriptionSummary();
    }, [selectedPlan, billingAddress?.country]);

    useEffect(() => {
      if (setPayableAmount) {
        setPayableAmount(totalPayable);
      }
    }, [totalPayable, setPayableAmount]);

    useEffect(() => {
      if (setBillingAddressParent) {
        setBillingAddressParent(billingAddress);
      }
    }, []);

    useImperativeHandle(ref, () => ({
      validate: () => {
        let newErrors: any = {};
        if (!billingAddress.line1)
          newErrors.line1 = "Address Line 1 is required";
        if (!billingAddress.city) newErrors.city = "City is required";
        if (!billingAddress.state) newErrors.state = "State is required";
        if (!billingAddress.postal_code)
          newErrors.postal_code = "Postal Code is required";
        if (!billingAddress.country) newErrors.country = "Country is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      },
      getValues: () => billingAddress,
    }));

    return (
      <>
        <Grid2 container spacing={3}>
          <Grid2
            size={{ xs: 12, md: 6 }}
            container
            direction="column"
            spacing={3}
          >
            <Grid2>
              <Typography
                variant="subtitle1"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                gutterBottom
              >
                Upgrade to {selectedPlan?.planTitle}
              </Typography>

              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  bgcolor: "grey.50",
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: "text.secondary",
                    letterSpacing: 1,
                    fontWeight: 500,
                    mb: 1,
                  }}
                  gutterBottom
                >
                  Selected Plan
                </Typography>
                <Typography
                  variant="h6"
                  color="primary"
                  gutterBottom
                  sx={{ textTransform: "uppercase" }}
                >
                  {selectedPlan?.planTitle}{" "}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ mb: 2 }}
                >
                  {selectedPlan?.planDescription}
                </Typography>

                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ cursor: "pointer", mt: 1 }}
                  onClick={() => setShowFeatures(!showFeatures)}
                >
                  {showFeatures ? "Hide Features" : "View Features"}
                </Typography>

                {showFeatures && (
                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Feature</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Limit</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Status</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentPlan?.details?.[0]?.features?.map(
                        (feature: any, index: number) => (
                          <TableRow
                            key={index}
                            sx={{
                              "& .MuiTableCell-root": {
                                bgcolor: index % 2 ? "grey.50" : "white",
                              },
                            }}
                          >
                            <TableCell>
                              {feature.feature_key
                                .replace("CONNECT_", "")
                                .replaceAll("_", " ")
                                .toLowerCase()
                                .replace(/\b\w/g, (c: any) => c.toUpperCase())}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={feature.limit}
                                color="primary"
                                size="small"
                                sx={{ width: "50px" }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <CheckCircleIcon
                                color="success"
                                fontSize="small"
                              />
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Grid2>

            <Grid2>
              <Typography
                variant="subtitle1"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                gutterBottom
              >
                Billing To
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12 }}>
                  <TextField
                    label="Address Line 1"
                    fullWidth
                    size="small"
                    value={billingAddress?.line1}
                    error={!!errors.line1}
                    onChange={(e) =>
                      handleAddressChange("line1", e.target.value)
                    }
                    slotProps={{ htmlInput: { maxLength: 100 } }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  <TextField
                    label="Address Line 2"
                    fullWidth
                    size="small"
                    value={billingAddress?.line2}
                    error={!!errors.line2}
                    onChange={(e) =>
                      handleAddressChange("line2", e.target.value)
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  <TextField
                    label="State"
                    fullWidth
                    size="small"
                    value={billingAddress?.state}
                    error={!!errors.state}
                    onChange={(e) =>
                      handleAddressChange("state", e.target.value)
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 6 }}>
                  <TextField
                    label="City"
                    fullWidth
                    size="small"
                    value={billingAddress?.city}
                    error={!!errors.city}
                    onChange={(e) =>
                      handleAddressChange("city", e.target.value)
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 6 }}>
                  <TextField
                    label="Postal Code"
                    fullWidth
                    size="small"
                    value={billingAddress?.postal_code}
                    error={!!errors.postal_code}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);
                      handleAddressChange("postal_code", value);
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  <TextField
                    select
                    label="Country"
                    fullWidth
                    size="small"
                    value={billingAddress.country}
                    onChange={(e) =>
                      handleAddressChange("country", e.target.value)
                    }
                  >
                    {countries.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>
              </Grid2>
            </Grid2>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <Typography
              variant="subtitle1"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              gutterBottom
            >
              Billing Summary
            </Typography>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box display="flex" alignItems="flex-start" gap={1}>
                <img
                  src="/assets/images/A-Realtime-Tech.svg"
                  alt="Company Logo"
                  style={{ width: 80, height: 60 }}
                />
              </Box>
              <Typography variant="subtitle2" color="text.secondary">
                Billing ID: {provisionalInvoiceNumber}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                bgcolor: "#fafafa",
              }}
            >
              <Grid2 container spacing={1}>
                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Plan Upgrade Charges
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6 }} sx={{ textAlign: "right" }}>
                  <Typography variant="body2">
                    ${subscriptionSumamry?.basePrice.toFixed(2)}
                  </Typography>
                </Grid2>

                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Additional Usage Charges
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Period: (
                    {formatInvoiceDate(
                      subscriptionSumamry?.additionalCharges?.messages
                        ?.from_date,
                    )}
                    –
                    {formatInvoiceDate(
                      subscriptionSumamry?.additionalCharges?.messages?.to_date,
                    )}
                    )
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6 }} sx={{ textAlign: "right" }}>
                  <Typography variant="body2">
                    ${subscriptionSumamry?.additionalCharges?.total.toFixed(2)}
                  </Typography>
                </Grid2>

                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Prorated Credit (Existing Plan)
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6 }} sx={{ textAlign: "right" }}>
                  <Typography variant="body2">
                    -${subscriptionSumamry?.discount?.discountAmount.toFixed(2)}
                  </Typography>
                </Grid2>
              </Grid2>

              <Divider sx={{ my: 1 }} />

              <Grid2 container spacing={1}>
                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="subtitle2">
                    Subtotal (Before Tax)
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6 }} sx={{ textAlign: "right" }}>
                  <Typography variant="subtitle2">
                    ${subscriptionSumamry?.summary?.subTotal}
                  </Typography>
                </Grid2>

                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Tax ({subscriptionSumamry?.summary?.taxPercent})%
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6 }} sx={{ textAlign: "right" }}>
                  <Typography variant="body2">
                    ${subscriptionSumamry?.summary?.taxAmount.toFixed(2)}
                  </Typography>
                </Grid2>
              </Grid2>

              <Divider sx={{ my: 1 }} />

              <Grid2
                container
                spacing={1}
                sx={{ bgcolor: "#f0f0f0", borderRadius: 1, p: 1, mt: 1 }}
              >
                <Grid2 size={{ xs: 6 }}>
                  <Typography variant="h6">Total Payable</Typography>
                </Grid2>
                <Grid2 size={{ xs: 6 }} sx={{ textAlign: "right" }}>
                  <Typography variant="h6" color="primary">
                    ${totalPayable.toFixed(2)}
                  </Typography>
                </Grid2>
              </Grid2>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 2, display: "block" }}
              >
                <strong>Note :</strong>This amount includes prorated costs and
                applicable taxes.
              </Typography>
            </Box>
          </Grid2>
        </Grid2>
      </>
    );
  },
);

export const UsageLimitsDialog = () => {
  const [usageLimits, setUsageLimits] = useState({
    monthlyLimit: "1000",
    alertThreshold: "80",
  });

  const handleChange = (field: string, value: string) => {
    setUsageLimits((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <>
      <Typography variant="body1" paragraph>
        Set monthly spending limits and configure usage alerts.
      </Typography>
      <ArtTextField
        label="Monthly Limit ($)"
        value={usageLimits.monthlyLimit}
        onChange={(e: any) => handleChange("monthlyLimit", e.target.value)}
        marginBottom={18}
        type="number"
      />
      <ArtTextField
        label="Alert Threshold (%)"
        value={usageLimits.alertThreshold}
        onChange={(e: any) => handleChange("alertThreshold", e.target.value)}
        marginBottom={18}
        type="number"
        helperText="Get notified when you reach this percentage of your monthly limit"
      />
      <ArtButton label="Save Limits" color="primary" />
    </>
  );
};

export const SubscriptionSummary = ({ planDetails }: any) => {
  const currentPlan = {
    name: planDetails?.name,
    status: "Active",
    amount: `${planDetails?.amount}/month`,
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="body1">Plan name</Typography>
        <Typography variant="body1" className="text-capitalize">
          {currentPlan.name}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="body1">License price</Typography>
        <Typography variant="body1">{currentPlan.amount}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="body1">Tax</Typography>
        <Typography variant="body1">$0</Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="body1">Additional</Typography>
        <Typography variant="body1">$0</Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="body1">Dues</Typography>
        <Typography variant="body1">$0</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="subtitle1">
          <strong>Total amount payable</strong>
        </Typography>
        <Typography variant="subtitle2">
          <strong>{currentPlan.amount}</strong>
        </Typography>
      </Box>
    </>
  );
};

interface AddonPurchaseConfirmationProps {
  addon: any;
  remainingDays: number;
  cycleDays: number;
  onCancel: () => void;
  onConfirm: (quantity: number) => void;
}

export const AddonPurchaseConfirmation: React.FC<
  AddonPurchaseConfirmationProps
> = ({ addon, remainingDays, cycleDays, onCancel, onConfirm }) => {
  const featureCode = formatFeatureCode(addon.feature?.code);
  const theme = useTheme();
  const [quantity, setQuantity] = useState(1);

  const currency =
    addon?.currency ||
    addon?.pricing?.currency ||
    addon?.pricings?.[0]?.currency ||
    "INR";

  const unitPrice = addon.price || 0;
  const billingInterval = addon.billing_interval || "monthly";
  const pricePerDay = unitPrice / cycleDays;
  const proratedUnitCost = pricePerDay * remainingDays;
  const totalProratedCost = proratedUnitCost * quantity;
  const totalFullCycleCost = unitPrice * quantity;
  const totalLimitBoost = (addon.limit_grant || 0) * quantity;

  const handleIncrement = () => setQuantity((prev) => prev + 1);
  const handleDecrement = () => setQuantity((prev) => Math.max(1, prev - 1));

  const costBreakdownRows = [
    {
      label: `Unit price (${billingInterval})`,
      value: `${currency} ${unitPrice.toFixed(2)}`,
    },
    { label: "Daily rate", value: `${currency} ${pricePerDay.toFixed(4)}/day` },
    { label: "Days remaining in cycle", value: `${remainingDays} days` },
    {
      label: "Prorated cost per unit",
      value: `${currency} ${proratedUnitCost.toFixed(2)}`,
    },
    { label: "Quantity", value: `× ${quantity}` },
  ];

  return (
    <Box p={2}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Confirm Purchase
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        You are about to purchase <strong>{addon.name}</strong>. The cost is
        automatically prorated to align with your base subscription cycle.
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} mb={1}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              "& .MuiSvgIcon-root": { fontSize: 20 },
            }}
          >
            {getCategoryIcon(addon.feature?.code)}
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {addon.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              +{addon.limit_grant} {featureCode || ""} per unit
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box
          display="flex"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              Quantity
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Each unit adds +{addon.limit_grant} {featureCode || ""}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              size="small"
              onClick={handleDecrement}
              disabled={quantity <= 1}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                width: 32,
                height: 32,
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                minWidth: 40,
                textAlign: "center",
              }}
            >
              {quantity}
            </Typography>
            <IconButton
              size="small"
              onClick={handleIncrement}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                width: 32,
                height: 32,
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box
          mt={1.5}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.success.main, 0.08),
          }}
        >
          <Typography variant="body2" fontWeight={600} color="success.main">
            Total boost: +{totalLimitBoost} {featureCode || ""}
          </Typography>
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, bgcolor: "rgba(0,0,0,0.02)" }}
      >
        <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ whiteSpace: "nowrap" }}
          >
            Cost Breakdown
          </Typography>
        </Box>

        {costBreakdownRows.map(({ label, value }) => (
          <Box
            key={label}
            display="flex"
            justifyContent="space-between"
            mb={0.75}
          >
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ whiteSpace: "nowrap" }}
            >
              {value}
            </Typography>
          </Box>
        ))}

        <Divider sx={{ my: 1.5 }} />

        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="subtitle2" fontWeight={700}>
            Total Due Now (Prorated)
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} color="primary.main">
            {totalProratedCost.toFixed(2)}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            Full cycle cost ({billingInterval}, ×{quantity})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {totalFullCycleCost.toFixed(2)}
          </Typography>
        </Box>
      </Paper>

      <Box display="flex" gap={2} justifyContent="flex-end" whiteSpace="nowrap">
        <ArtButton
          label="Cancel"
          variant="outlined"
          color="error"
          onClick={onCancel}
        />
        <ArtButton
          label={`Pay ${currency} ${totalProratedCost.toFixed(2)}`}
          variant="contained"
          color="primary"
          onClick={() => onConfirm(quantity)}
        />
      </Box>
    </Box>
  );
};

export const AddonOrderSummaryDialog = ({
  addon,
  quantity,
  onProceedToPayment,
  onBack,
}: any) => {
  const theme = useTheme();
  const apiservice = useApiService();
  const [preview, setPreview] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [billingAddress, setBillingAddress] = useState<any>(null);
  const [loadingAddress, setLoadingAddress] = useState(true);

  const [gstNumber, setGstNumber] = useState<string>("");
  const [editingGst, setEditingGst] = useState(false);
  const [gstInput, setGstInput] = useState("");
  const [savingGst, setSavingGst] = useState(false);

  const fetchGstNumber = async () => {
    try {
      const res = await apiservice.get("/organisation/gst-number", { showErrorSnack: false });
      const gst = res?.data?.data?.gst_number || "";
      setGstNumber(gst);
      setGstInput(gst);
    } catch (error) {
      console.error("Failed to fetch GST number:", error);
    }
  };

  const handleSaveGst = async () => {
    setSavingGst(true);
    try {
      await apiservice.put("/organisation/gst-number", {
        gst_number: gstInput.trim(),
      });
      setGstNumber(gstInput.trim());
      setEditingGst(false);
      apiservice.showSnack("GST number updated successfully", "success");
    } catch (error) {
      console.error("Failed to save GST number:", error);
      apiservice.showSnack("Failed to update GST number", "error");
    } finally {
      setSavingGst(false);
    }
  };

  const handleClearGst = async () => {
    setSavingGst(true);
    try {
      await apiservice.put("/organisation/gst-number", { gst_number: "" });
      setGstNumber("");
      setGstInput("");
      setEditingGst(false);
      apiservice.showSnack("GST number cleared successfully", "success");
    } catch (error) {
      console.error("Failed to clear GST number:", error);
      apiservice.showSnack("Failed to clear GST number", "error");
    } finally {
      setSavingGst(false);
    }
  };

  const fetchPreview = async () => {
    setLoadingPreview(true);
    try {
      const response = await apiservice.post(`/payment/addon/preview`, {
        addon_id: addon.id,
        quantity,
        application_code: addon.feature?.application?.code,
      });
      if (response?.status === 200) {
        setPreview(response.data.data);
      }
    } catch (error: any) {
      apiservice.showSnack(
        error?.response?.data?.message || "Failed to load addon preview",
        "error",
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  const fetchBillingAddress = async () => {
    setLoadingAddress(true);
    try {
      const response = await apiservice.get("/organisation/billing-address");
      if (response?.status === 200 && response.data?.data) {
        setBillingAddress(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch billing address:", error);
    } finally {
      setLoadingAddress(false);
    }
  };

  useEffect(() => {
    fetchPreview();
    fetchBillingAddress();
    fetchGstNumber();
  }, []);

  const basePrice = preview ? (preview.base_price / 100).toFixed(2) : "0.00";
  const taxPercent = preview?.tax_percent || 0;
  const taxAmount = preview ? (preview.tax_amount / 100).toFixed(2) : "0.00";
  const totalAmount = preview
    ? (preview.total_amount / 100).toFixed(2)
    : "0.00";
  const currency = preview?.currency || "USD";
  const addonFullPrice = preview?.addon_price ?? addon?.price ?? 0;
  const proratedCost = preview ? preview.prorated_cost : 0;
  // const remainingDays = preview?.remaining_days;
  // const cycleDays = preview?.cycle_days;

  return (
    <Box>
      {/* Back button */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          variant="text"
          size="small"
          onClick={onBack}
          sx={{ textTransform: "none", fontWeight: 500, mr: 1 }}
        >
          ← Back
        </Button>
      </Box>

      <Grid2 container spacing={3}>
        {/* Left: Addon + Billing Address */}
        <Grid2 size={{ xs: 12, md: 7 }}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 1, fontWeight: 600 }}
            >
              Add-on
            </Typography>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
              mb={1.5}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                className="text-capitalize"
              >
                {addon?.name}
              </Typography>
              <Chip
                label={`× ${quantity}`}
                color="primary"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>

            {addon?.description && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                {addon.description}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Unit price
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {currency} {Number(addonFullPrice).toFixed(2)} /{" "}
                  {addon?.billing_interval || "cycle"}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* GST Number - compact inline row */}
          <Paper
            variant="outlined"
            sx={{
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {!editingGst ? (
              <>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    GST Number
                  </Typography>
                  {gstNumber ? (
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ letterSpacing: 0.5, fontFamily: "monospace" }}
                    >
                      {gstNumber}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{ cursor: "pointer", fontWeight: 500 }}
                      onClick={() => setEditingGst(true)}
                    >
                      + Add for invoices
                    </Typography>
                  )}
                </Box>
                {gstNumber && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setGstInput(gstNumber);
                      setEditingGst(true);
                    }}
                    sx={{
                      textTransform: "none",
                      minWidth: "auto",
                      fontWeight: 500,
                    }}
                  >
                    Edit
                  </Button>
                )}
              </>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ width: "100%" }}
                flexWrap="wrap"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  GST Number
                </Typography>
                <TextField
                  size="small"
                  placeholder="Enter GST number"
                  value={gstInput}
                  onChange={(e) => setGstInput(e.target.value)}
                  sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveGst}
                  disabled={savingGst}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Save
                </Button>
                {gstNumber && (
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    onClick={handleClearGst}
                    disabled={savingGst}
                    sx={{ textTransform: "none", fontWeight: 500 }}
                  >
                    Clear
                  </Button>
                )}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setEditingGst(false);
                    setGstInput(gstNumber);
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <LocationOn sx={{ fontSize: 20, color: "text.secondary" }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Billing Address
              </Typography>
            </Box>
            {loadingAddress ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : billingAddress ? (
              <Box>
                <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                  {billingAddress.line1}
                </Typography>
                {billingAddress.line2 && (
                  <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                    {billingAddress.line2}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {[billingAddress.city, billingAddress.state]
                    .filter(Boolean)
                    .join(", ")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {billingAddress.country}
                  {billingAddress.postal_code
                    ? ` - ${billingAddress.postal_code}`
                    : ""}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No billing address saved.
              </Typography>
            )}
          </Paper>
        </Grid2>

        {/* Right: Order Summary */}
        <Grid2 size={{ xs: 12, md: 5 }} sx={{ display: "flex" }}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" fontWeight={700} mb={2}>
              Order Summary
            </Typography>

            {loadingPreview ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Add-on
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    className="text-capitalize"
                  >
                    {addon?.name}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Quantity
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    × {quantity}
                  </Typography>
                </Box>

                {proratedCost > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1.5}>
                    <Typography variant="body2" color="text.secondary">
                      Prorated cost
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {currency} {Number(proratedCost).toFixed(2)}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Base Price
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {currency} {basePrice}
                  </Typography>
                </Box>

                {taxPercent > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1.5}>
                    <Typography variant="body2" color="text.secondary">
                      Tax ({taxPercent}%)
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {currency} {taxAmount}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    mb: { xs: 0, sm: 3 },
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      Amount Due
                    </Typography>
                    <Typography variant="h5" color="primary" fontWeight={800}>
                      {currency} {totalAmount}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            <Box sx={{ mt: "auto", pt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={loadingPreview || !preview}
                onClick={() => onProceedToPayment(addon, quantity, preview)}
                sx={{
                  width: "100%",
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  mb: 1.5,
                  whiteSpace: "nowrap",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                }}
              >
                {loadingPreview
                  ? "Loading..."
                  : `Pay ${currency} ${totalAmount}`}
              </Button>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.5}
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
                <Typography variant="caption" color="text.secondary">
                  Secured payment. Your data is encrypted.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export const AddonSupportDialog = ({ addonName }: { addonName?: string }) => {
  const name = addonName || "this add-on";

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
        <InfoOutlinedIcon
          color="info"
          sx={{ mt: "4px", height: 30, width: 30 }}
        />

        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Want to purchase {name}?
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Add-on purchases are currently handled by our team to make sure the
            right limits and billing setup are applied to your account.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please contact support and we’ll help you complete the add-on
            purchase.
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "primary.light",
          borderRadius: 2,
          p: 2.5,
          backgroundColor: "#F5FAFF",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Need this add-on?
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Reach out to our support team and they’ll assist you with the next
          steps.
        </Typography>

        <Box
          sx={{
            p: 1,
            backgroundColor: "#ffffff",
            border: "1px solid",
            borderColor: "primary.light",
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <MailOutlineIcon color="primary" />
            <Typography
              component="a"
              href="mailto:info@aiotrix.com"
              variant="body1"
              sx={{
                color: "primary.main",
                fontWeight: 500,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              info@aiotrix.com
            </Typography>
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Our team will help you enable the correct add-on and complete the
            purchase.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export const UpgradeAllertDialog = ({ addonName }: { addonName?: string }) => {
  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
        <InfoOutlinedIcon
          color="warning"
          sx={{ mt: "4px", height: 30, width: 30 }}
        />

        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            You’re currently on the Sandbox Plan.
          </Typography>

          <Typography variant="body2" color="text.secondary">
            <span style={{ fontWeight: 800 }}>
              {addonName || "This add-on"}
            </span>{" "}
            is not available while your account is on the Sandbox plan.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            To purchase add-ons, you’ll need to upgrade your plan first.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Our team can help you choose the right plan and enable add-ons for
            your account.
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "primary.light",
          borderRadius: 2,
          p: 2.5,
          backgroundColor: "#F5FAFF",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Need to upgrade your plan?
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please contact our support team and they’ll guide you through the next
          steps.
        </Typography>

        <Box
          sx={{
            p: 1,
            backgroundColor: "#ffffff",
            border: "1px solid",
            borderColor: "primary.light",
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <MailOutlineIcon color="primary" />
            <Typography
              component="a"
              href="mailto:info@aiotrix.com"
              variant="body1"
              sx={{
                color: "primary.main",
                fontWeight: 500,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              info@aiotrix.com
            </Typography>
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Upgrade your plan first, then you’ll be able to purchase add-ons.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
